import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  BuildingOffice2Icon,
  ChartBarIcon,
  CurrencyDollarIcon,
  LinkIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import AnalyticsDashboard from '../dashboards/AnalyticsDashboard';
import StatsCard from '../ui/StatsCard';
import { fetchAdminAnalytics, formatTrendData } from '../../utils/analytics';
import { platformRequestConfig } from '../../utils/platform/platformRequest';
import toast from 'react-hot-toast';
import PlatformClientStateBreakdown from './PlatformClientStateBreakdown';
import Button from '../ui/Button';
import { StatsRowSkeleton } from '../ui/PageSkeleton';

function CompanyComparisonRow({ company, maxReferrals, onSelect, pa }) {
  const referrals = company.stats?.referrals || 0;
  const width = maxReferrals > 0 ? Math.max(8, (referrals / maxReferrals) * 100) : 0;

  return (
    <button
      type="button"
      onClick={() => onSelect?.(company.id)}
      className="w-full rounded-xl border border-secondary-200/80 p-4 text-left transition hover:border-orange-200 hover:bg-orange-50/30   "
    >
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-secondary-900 ">{company.name}</p>
          <p className="text-xs text-secondary-500 ">
            {company.stats?.clients || 0} {pa.clientsShort} · {company.stats?.vendors || 0} {pa.vendorsShort}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold text-orange-600">{referrals} {pa.referralsShort}</p>
          <p className="text-xs text-secondary-500 ">${(company.stats?.revenue || 0).toLocaleString()}</p>
        </div>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-secondary-100 ">
        <div className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-500" style={{ width: `${width}%` }} />
      </div>
    </button>
  );
}

function PlatformAnalyticsView({ companies = [], overview = null, selectedCompanyId = null, onSelectCompany }) {
  const { t } = useLanguage();
  const pa = t.platformAdmin;
  const adminT = t.admin;
  const statsCardT = t.statsCard;
  const [adminAnalytics, setAdminAnalytics] = useState(null);
  const [adminOverview, setAdminOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const config = platformRequestConfig(selectedCompanyId);
      const [analyticsResponse, overviewResponse] = await Promise.all([
        fetchAdminAnalytics(selectedCompanyId),
        axios.get('/api/admin/analytics/overview', config),
      ]);
      setAdminAnalytics(analyticsResponse);
      setAdminOverview(overviewResponse.data);
    } catch (error) {
      console.error('Failed to load platform analytics', error);
      setLoadError(true);
      toast.error(pa.failedToLoadAnalytics);
    } finally {
      setLoading(false);
    }
  }, [pa.failedToLoadAnalytics, selectedCompanyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const scopedCompanies = useMemo(() => {
    if (!selectedCompanyId) return companies;
    return companies.filter((company) => company.id === selectedCompanyId);
  }, [companies, selectedCompanyId]);

  const maxReferrals = useMemo(
    () => Math.max(...scopedCompanies.map((company) => company.stats?.referrals || 0), 1),
    [scopedCompanies]
  );

  const totals = overview?.totals || {};
  const clientStates = selectedCompanyId
    ? (adminOverview?.client_states || {})
    : (overview?.client_states || adminOverview?.client_states || {});

  if (loadError && !adminOverview) {
    return (
      <div className="rounded-2xl border border-dashed border-secondary-300 p-10 text-center ">
        <p className="text-secondary-600 ">{pa.failedToLoadAnalytics}</p>
        <Button
          className="mt-4"
          onClick={() => {
            void fetchData();
          }}
        >
          {pa.retry}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 xl:space-y-7">
      {loading ? (
        <StatsRowSkeleton />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatsCard
            title={adminT.totalRevenue}
            value={`$${(adminOverview?.revenue?.total ?? totals.revenue ?? 0).toLocaleString()}`}
            change={formatTrendData(adminAnalytics, 'revenue')?.change || 0}
            changeType={formatTrendData(adminAnalytics, 'revenue')?.changeType || 'neutral'}
            icon={CurrencyDollarIcon}
            gradient="from-emerald-500 to-teal-500"
            description={selectedCompanyId ? pa.companyScopedAnalytics : pa.platformRevenue}
            trend={formatTrendData(adminAnalytics, 'revenue')?.data || []}
            periodText={statsCardT.vsLastPeriod}
            trendPeriodText={statsCardT.last7Days}
          />
          <StatsCard
            title={adminT.totalUsers}
            value={adminOverview?.users?.total ?? ((totals.clients ?? 0) + (totals.vendors ?? 0) + (totals.admins ?? 0))}
            change={formatTrendData(adminAnalytics, 'users')?.change || 0}
            changeType={formatTrendData(adminAnalytics, 'users')?.changeType || 'neutral'}
            icon={UsersIcon}
            gradient="from-orange-500 to-amber-500"
            description={selectedCompanyId ? pa.companyScopedAnalytics : pa.allUsers}
            trend={formatTrendData(adminAnalytics, 'users')?.data || []}
            periodText={statsCardT.vsLastPeriod}
            trendPeriodText={statsCardT.last7Days}
          />
          <StatsCard
            title={adminT.totalReferrals}
            value={adminOverview?.referrals?.total ?? totals.referrals ?? 0}
            change={formatTrendData(adminAnalytics, 'referrals')?.change || 0}
            changeType={formatTrendData(adminAnalytics, 'referrals')?.changeType || 'neutral'}
            icon={LinkIcon}
            gradient="from-red-500 to-pink-500"
            description={selectedCompanyId ? pa.companyScopedAnalytics : pa.acrossAllCompanies}
            trend={formatTrendData(adminAnalytics, 'referrals')?.data || []}
            periodText={statsCardT.vsLastPeriod}
            trendPeriodText={statsCardT.last7Days}
          />
          <StatsCard
            title={pa.totalCompanies}
            value={selectedCompanyId ? 1 : totals.companies ?? companies.length}
            icon={BuildingOffice2Icon}
            gradient="from-violet-500 to-indigo-500"
            description={selectedCompanyId ? pa.companyScopedAnalytics : pa.activeTenants}
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3 xl:gap-6">
        <section className="xl:col-span-1 space-y-4">
          <div className="flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-orange-500" />
            <h3 className="text-lg font-semibold text-secondary-900 ">{pa.companyComparison}</h3>
          </div>
          <div className="space-y-3">
            {scopedCompanies.length > 0 ? (
              scopedCompanies.map((company) => (
                <CompanyComparisonRow
                  key={company.id}
                  company={company}
                  maxReferrals={maxReferrals}
                  onSelect={onSelectCompany}
                  pa={pa}
                />
              ))
            ) : (
              <p className="rounded-xl border border-dashed border-secondary-300 p-6 text-sm text-secondary-500  ">
                {pa.noCompanies}
              </p>
            )}
          </div>
        </section>

        <section className="xl:col-span-2 card-premium p-5">
          <h3 className="mb-4 text-lg font-semibold text-secondary-900 ">{pa.clientStateBreakdown}</h3>
          <PlatformClientStateBreakdown clientStates={clientStates} />
        </section>
      </div>

      {!loading ? (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <AnalyticsDashboard presetCompanyId={selectedCompanyId} hidePageHeader />
        </motion.div>
      ) : null}
    </div>
  );
}

export default PlatformAnalyticsView;
